// src/components/configuracion/CatalogosTab/CatalogosContainer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

// Componente presentacional
import CatalogosPresentation from './CatalogosPresentation';
import EditIcon from '@mui/icons-material/Edit';

// Hooks personalizados 
import { useApi, useForm, useNotification } from '../../../hooks';

// Servicios
import { catalogosService } from '../../../services/catalogosService';

/**
 * Validaciones para formularios de catálogos
 */
const validationRules = {
  codigo: {
    required: true,
    message: 'Código es requerido'
  },
  nombre: {
    required: true,
    message: 'Nombre es requerido'
  }
};

/**
 * Componente contenedor para la gestión de catálogos contables
 * Se encarga de la lógica de negocio, estado y comunicación con la API
 */
const CatalogosContainer = () => {
  const { empresaActiva } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();
  
  // Estado para tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para diálogos
  const [openCuentaDialog, setOpenCuentaDialog] = useState(false);
  const [openCentroDialog, setOpenCentroDialog] = useState(false);
  const [openClasificadorDialog, setOpenClasificadorDialog] = useState(false);
  
  // Estados para elementos seleccionados
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [centroSeleccionado, setCentroSeleccionado] = useState(null);
  const [clasificadorSeleccionado, setClasificadorSeleccionado] = useState(null);
  
  // API hooks para cargar catálogos
  const { 
    data: cuentas, 
    loading: loadingCuentas, 
    error: errorCuentas,
    execute: loadCuentas 
  } = useApi(catalogosService.getCuentas);
  
  const { 
    data: centros, 
    loading: loadingCentros, 
    error: errorCentros,
    execute: loadCentros 
  } = useApi(catalogosService.getCentros);
  
  const { 
    data: clasificadores, 
    loading: loadingClasificadores, 
    error: errorClasificadores,
    execute: loadClasificadores 
  } = useApi(catalogosService.getClasificadores);
  
  // API hooks para operaciones CRUD
  const { loading: guardandoCuenta, execute: guardarCuenta } = useApi(catalogosService.crearCuenta);
  const { loading: guardandoCentro, execute: guardarCentro } = useApi(catalogosService.crearCentro);
  const { loading: guardandoClasificador, execute: guardarClasificador } = useApi(catalogosService.crearClasificador);
  
  // Hooks para formularios
  const cuentaForm = useForm({
    codigo: '',
    nombre: '',
    tipo: '',
    activa: 1,
    rut_empresa: empresaActiva?.rut
  }, validationRules);
  
  const centroForm = useForm({
    codigo: '',
    nombre: '',
    activo: 1,
    rut_empresa: empresaActiva?.rut
  }, validationRules);
  
  const clasificadorForm = useForm({
    codigo: '',
    nombre: '',
    tipo: '',
    activo: 1,
    rut_empresa: empresaActiva?.rut
  }, validationRules);
  
  // Cargar catálogos
  const cargarCatalogos = useCallback(async () => {
    if (!empresaActiva) return;
    
    loadCuentas(true, empresaActiva.rut);
    loadCentros(true, empresaActiva.rut);
    loadClasificadores(null, true, empresaActiva.rut);
  }, [empresaActiva, loadCuentas, loadCentros, loadClasificadores]);
  
  // Cargar datos iniciales cuando cambia la empresa activa
  useEffect(() => {
    if (empresaActiva) {
      cargarCatalogos();
    }
  }, [empresaActiva, cargarCatalogos]);
  
  // Manejar cambio de pestaña
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);
  
  // Funciones para manejo de diálogos
  const handleOpenCuentaDialog = useCallback((cuenta = null) => {
    setCuentaSeleccionada(cuenta);
    if (cuenta) {
      cuentaForm.setValues({
        codigo: cuenta.codigo,
        nombre: cuenta.nombre,
        tipo: cuenta.tipo || '',
        activa: cuenta.activa,
        id: cuenta.id,
        rut_empresa: empresaActiva.rut
      });
    } else {
      cuentaForm.reset();
      cuentaForm.setValues({ rut_empresa: empresaActiva.rut });
    }
    setOpenCuentaDialog(true);
  }, [cuentaForm, empresaActiva]);
  
  const handleOpenCentroDialog = useCallback((centro = null) => {
    setCentroSeleccionado(centro);
    if (centro) {
      centroForm.setValues({
        codigo: centro.codigo,
        nombre: centro.nombre,
        activo: centro.activo,
        id: centro.id,
        rut_empresa: empresaActiva.rut
      });
    } else {
      centroForm.reset();
      centroForm.setValues({ rut_empresa: empresaActiva.rut });
    }
    setOpenCentroDialog(true);
  }, [centroForm, empresaActiva]);
  
  const handleOpenClasificadorDialog = useCallback((clasificador = null) => {
    setClasificadorSeleccionado(clasificador);
    if (clasificador) {
      clasificadorForm.setValues({
        codigo: clasificador.codigo,
        nombre: clasificador.nombre,
        tipo: clasificador.tipo || '',
        activo: clasificador.activo,
        id: clasificador.id,
        rut_empresa: empresaActiva.rut
      });
    } else {
      clasificadorForm.reset();
      clasificadorForm.setValues({ rut_empresa: empresaActiva.rut });
    }
    setOpenClasificadorDialog(true);
  }, [clasificadorForm, empresaActiva]);
  
  // Funciones para guardar catálogos
  const handleGuardarCuenta = useCallback(async () => {
    if (!cuentaForm.validateForm()) return;
    
    try {
      await guardarCuenta(cuentaForm.values);
      showNotification("Cuenta guardada correctamente", "success");
      setOpenCuentaDialog(false);
      loadCuentas(true, empresaActiva.rut);
    } catch (err) {
      // Error manejado por useApi
      console.error("Error al guardar cuenta:", err);
    }
  }, [cuentaForm, guardarCuenta, showNotification, loadCuentas, empresaActiva]);
  
  const handleGuardarCentro = useCallback(async () => {
    if (!centroForm.validateForm()) return;
    
    try {
      await guardarCentro(centroForm.values);
      showNotification("Centro guardado correctamente", "success");
      setOpenCentroDialog(false);
      loadCentros(true, empresaActiva.rut);
    } catch (err) {
      // Error manejado por useApi
      console.error("Error al guardar centro:", err);
    }
  }, [centroForm, guardarCentro, showNotification, loadCentros, empresaActiva]);
  
  const handleGuardarClasificador = useCallback(async () => {
    if (!clasificadorForm.validateForm()) return;
    
    try {
      await guardarClasificador(clasificadorForm.values);
      showNotification("Clasificador guardado correctamente", "success");
      setOpenClasificadorDialog(false);
      loadClasificadores(null, true, empresaActiva.rut);
    } catch (err) {
      // Error manejado por useApi
      console.error("Error al guardar clasificador:", err);
    }
  }, [clasificadorForm, guardarClasificador, showNotification, loadClasificadores, empresaActiva]);

  // Determinar si un elemento es editable
  const isEditable = useCallback((item) => {
    return item && item.rut_empresa !== "DEFAULT";
  }, []);
  
  // Configuración de columnas para tablas
  const cuentasColumns = [
    { field: 'codigo', headerName: 'Código' },
    { field: 'nombre', headerName: 'Nombre' },
    { field: 'tipo', headerName: 'Tipo', renderCell: val => val || "-" },
    { 
      field: 'activa', 
      headerName: 'Estado', 
      align: 'center',
      renderCell: val => val ? 'Activa' : 'Inactiva'
    },
    {
      field: 'rut_empresa',
      headerName: 'Origen',
      align: 'center',
      renderCell: val => val === "DEFAULT" ? 'General' : 'Empresa'
    }
  ];
  
  const centrosColumns = [
    { field: 'codigo', headerName: 'Código' },
    { field: 'nombre', headerName: 'Nombre' },
    { 
      field: 'activo', 
      headerName: 'Estado', 
      align: 'center',
      renderCell: val => val ? 'Activo' : 'Inactivo'
    },
    {
      field: 'rut_empresa',
      headerName: 'Origen',
      align: 'center',
      renderCell: val => val === "DEFAULT" ? 'General' : 'Empresa'
    }
  ];
  
  const clasificadoresColumns = [
    { field: 'codigo', headerName: 'Código' },
    { field: 'nombre', headerName: 'Nombre' },
    { field: 'tipo', headerName: 'Tipo', renderCell: val => val || "-" },
    { 
      field: 'activo', 
      headerName: 'Estado', 
      align: 'center',
      renderCell: val => val ? 'Activo' : 'Inactivo'
    },
    {
      field: 'rut_empresa',
      headerName: 'Origen',
      align: 'center',
      renderCell: val => val === "DEFAULT" ? 'General' : 'Empresa'
    }
  ];
  
  // Acciones para tablas
  const getTableActions = useCallback(() => {
    return {
      cuentasActions: [
        {
          icon: <EditIcon fontSize="small" />,
          tooltip: 'Editar cuenta',
          onClick: handleOpenCuentaDialog,
          disabled: (row) => !isEditable(row)
        }
      ],
      centrosActions: [
        {
          icon: <EditIcon fontSize="small" />,
          tooltip: 'Editar centro',
          onClick: handleOpenCentroDialog,
          disabled: (row) => !isEditable(row)
        }
      ],
      clasificadoresActions: [
        {
          icon: <EditIcon fontSize="small" />,
          tooltip: 'Editar clasificador',
          onClick: handleOpenClasificadorDialog,
          disabled: (row) => !isEditable(row)
        }
      ]
    };
  }, [handleOpenCuentaDialog, handleOpenCentroDialog, handleOpenClasificadorDialog, isEditable]);

  // Las acciones para los diferentes tipos de tablas
  const { cuentasActions, centrosActions, clasificadoresActions } = getTableActions();

  // Manejar cierre de diálogos
  const handleCloseCuentaDialog = useCallback(() => setOpenCuentaDialog(false), []);
  const handleCloseCentroDialog = useCallback(() => setOpenCentroDialog(false), []);
  const handleCloseClasificadorDialog = useCallback(() => setOpenClasificadorDialog(false), []);

  // Pasar props al componente presentacional
  return (
    <CatalogosPresentation 
      // Estado general
      empresaActiva={empresaActiva}
      tabValue={tabValue}
      onTabChange={handleTabChange}
      
      // Cuentas
      cuentas={cuentas}
      loadingCuentas={loadingCuentas}
      errorCuentas={errorCuentas}
      cuentasColumns={cuentasColumns}
      cuentasActions={cuentasActions}
      openCuentaDialog={openCuentaDialog}
      onCloseCuentaDialog={handleCloseCuentaDialog}
      onOpenCuentaDialog={handleOpenCuentaDialog}
      onGuardarCuenta={handleGuardarCuenta}
      cuentaSeleccionada={cuentaSeleccionada}
      guardandoCuenta={guardandoCuenta}
      cuentaForm={cuentaForm}
      
      // Centros
      centros={centros}
      loadingCentros={loadingCentros}
      errorCentros={errorCentros}
      centrosColumns={centrosColumns}
      centrosActions={centrosActions}
      openCentroDialog={openCentroDialog}
      onCloseCentroDialog={handleCloseCentroDialog}
      onOpenCentroDialog={handleOpenCentroDialog}
      onGuardarCentro={handleGuardarCentro}
      centroSeleccionado={centroSeleccionado}
      guardandoCentro={guardandoCentro}
      centroForm={centroForm}
      
      // Clasificadores
      clasificadores={clasificadores}
      loadingClasificadores={loadingClasificadores}
      errorClasificadores={errorClasificadores}
      clasificadoresColumns={clasificadoresColumns}
      clasificadoresActions={clasificadoresActions}
      openClasificadorDialog={openClasificadorDialog}
      onCloseClasificadorDialog={handleCloseClasificadorDialog}
      onOpenClasificadorDialog={handleOpenClasificadorDialog}
      onGuardarClasificador={handleGuardarClasificador}
      clasificadorSeleccionado={clasificadorSeleccionado}
      guardandoClasificador={guardandoClasificador}
      clasificadorForm={clasificadorForm}
    />
  );
};

export default CatalogosContainer;